import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import type { RegionsRepository } from "../domain";
import {
  archivedRegionFromDto,
  archivedRegionMatchFromDto,
  regionCreateToDto,
  regionFromDto,
  regionUpdateToDto,
} from "./organizationMappers";
import {
  archivedRegionListResponseDtoSchema,
  archivedRegionMatchResponseDtoSchema,
  regionCreateWriteDtoSchema,
  regionListResponseDtoSchema,
  regionResponseDtoSchema,
  regionUpdateWriteDtoSchema,
  messageResponseDtoSchema,
} from "./organizationDtos";

const REGIONS_PATH = `${API_PREFIX}/regions`;

export function createRegionsHttpRepository(http: HttpClient): RegionsRepository {
  return {
    async list(query) {
      const response = await http.get(REGIONS_PATH, { params: query });
      return paginatedFromDto(
        regionListResponseDtoSchema.parse(response),
        regionFromDto,
      );
    },

    async getById(id) {
      const response = await http.get(`${REGIONS_PATH}/${id}`);
      return regionFromDto(regionResponseDtoSchema.parse(response).data);
    },

    async create(input) {
      const dto = regionCreateWriteDtoSchema.parse(regionCreateToDto(input));
      try {
        const response = await http.post(REGIONS_PATH, dto);
        const created = regionResponseDtoSchema.parse(response);
        const region = regionFromDto(created.data);
        return {
          status: "created",
          region,
          ...(created.message !== undefined
            ? { responseMessage: created.message }
            : {}),
        };
      } catch (error) {
        // Same archived-match-as-409 shape every other create endpoint in
        // this app uses: nothing was created, the body just offers a
        // restore. Disambiguated by shape, not the `code` string.
        const archived =
          error instanceof HttpError && error.status === 409
            ? archivedRegionMatchResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (archived === undefined || !archived.success) throw error;
        return {
          status: "archived-match",
          match: archivedRegionMatchFromDto(archived.data.data),
          message: archived.data.message,
        };
      }
    },

    async update(id, input) {
      const dto = regionUpdateWriteDtoSchema.parse(regionUpdateToDto(input));
      const response = await http.patch(`${REGIONS_PATH}/${id}`, dto);
      return regionFromDto(regionResponseDtoSchema.parse(response).data);
    },

    async archive(id) {
      const response = await http.delete(`${REGIONS_PATH}/${id}`);
      messageResponseDtoSchema.parse(response);
    },

    async listArchived(query) {
      const response = await http.get(`${REGIONS_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedRegionListResponseDtoSchema.parse(response),
        archivedRegionFromDto,
      );
    },

    async restore(id) {
      const response = await http.patch(`${REGIONS_PATH}/${id}/restore`);
      const parsed = regionResponseDtoSchema.parse(response);
      const region = regionFromDto(parsed.data);
      return {
        ...region,
        ...(parsed.message !== undefined
          ? { responseMessage: parsed.message }
          : {}),
      };
    },
  };
}
